import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import { alpha } from '@mui/material/styles';

import React, { useMemo } from 'react';

import { TimezoneAbbreviationEnum, TranscriptMessage } from '_lib/api/apiTypes.shim';
import { List, ListProps } from '_lib/components/List/List';
import { ListItem, ListItemProps } from '_lib/components/ListItem/ListItem';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { Stack } from '_lib/components/Stack/Stack';
import { IconTypography } from '_lib/featureComponents/IconTypography/IconTypography';
import { TextBlockTypography } from '_lib/featureComponents/TextBoxTypography/TextBoxTypography';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { toSxArray } from '_lib/utils/helpers';

type AiCallTranscriptListProps = ListProps & {
  transcriptMessages: TranscriptMessage[];
  timezone: TimezoneAbbreviationEnum;
  listItemProps?: ListItemProps;
};

export const AiCallTranscriptList = (props: AiCallTranscriptListProps) => {
  const { transcriptMessages, timezone, listItemProps, ...listProps } = props;

  const sortedTranscriptMessages = useMemo(
    () => [...transcriptMessages].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()),
    [transcriptMessages]
  );
  const firstTimeStamp = sortedTranscriptMessages[0]?.time;

  return (
    <List {...listProps}>
      {sortedTranscriptMessages.map((transcriptItem, index) => {
        const diffValues = firstTimeStamp
          ? DateUtils.toDiffValues(firstTimeStamp, transcriptItem.time, { timezone })
          : null;

        const minutesPassedSinceFirstMessage = `${diffValues ? diffValues.minutes : 0}`.padStart(2, '0');
        const secondsPassedSinceFirstMessage = `${diffValues ? diffValues.seconds : 0}`.padStart(2, '0');
        const millisecondsPassedSinceFirstMessage = `${diffValues ? Math.round(diffValues.milliseconds / 10) * 10 : 0}`
          .padStart(2, '0')
          .slice(0, 2);

        return (
          <ListItem
            key={`${transcriptItem.time}-${index}`}
            {...listItemProps}
            sx={[
              (theme) => ({
                width: 'fit-content',
                maxWidth: '50%',
                paddingX: 1,
                paddingY: 0,
                marginBottom: 2,
                justifySelf: transcriptItem.role === 'user' ? 'flex-end' : 'flex-start',
                borderRadius: '8px',
                border:
                  transcriptItem.role === 'user'
                    ? `2px solid ${alpha(theme.palette.primary.main, 0.5)}`
                    : `2px solid ${alpha(theme.palette.infoSecondary.main, 0.5)}`
              }),
              ...toSxArray(listItemProps?.sx)
            ]}>
            <ListItemText
              primary={
                <Stack spacing={0}>
                  <IconTypography
                    sx={(theme) => ({
                      color:
                        transcriptItem.role === 'user' ? theme.palette.primary.main : theme.palette.infoSecondary.main
                    })}
                    startIcon={
                      transcriptItem.role === 'user' ? (
                        <PersonIcon fontSize='small' sx={(theme) => ({ color: theme.palette.primary.main })} />
                      ) : (
                        <AutoAwesomeIcon
                          fontSize='small'
                          sx={(theme) => ({ color: theme.palette.infoSecondary.main })}
                        />
                      )
                    }
                    text={transcriptItem.role === 'user' ? 'Caller:' : 'Assistant:'}
                  />
                  <TextBlockTypography variant='body2' text={transcriptItem.message} />
                </Stack>
              }
              secondary={`${DateUtils.format(transcriptItem.time, DateFormatEnum.TimeWithSeconds, {
                timezone,
                showTimezone: true
              })} [+${minutesPassedSinceFirstMessage}:${secondsPassedSinceFirstMessage}.${millisecondsPassedSinceFirstMessage}s]`}
            />
          </ListItem>
        );
      })}
    </List>
  );
};
